import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroImg from "@/assets/blog/fatty-liver-insulin-hero.webp";
import loopImg from "@/assets/blog/fatty-liver-insulin-loop.webp";
import habitsImg from "@/assets/blog/fatty-liver-insulin-habits.webp";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";


const SLUG = "fatty-liver-insulin-resistance-diabetes";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE = "Fatty Liver and Diabetes: Insulin Resistance | TruWe";
const HEADLINE =
  "Fatty Liver and Diabetes: How Liver Fat and Insulin Resistance Are Connected";
const PUBLISHED = "2026-08-21";
const DESCRIPTION =
  "Learn how fatty liver, insulin resistance, prediabetes and type 2 diabetes are connected — and what steps may support metabolic health.";

const FAQS = [
  {
    q: "Does fatty liver mean I have diabetes?",
    a: "No. Fatty liver and diabetes frequently occur together, but neither condition automatically proves that the other is present. Blood-sugar testing is needed to diagnose prediabetes or diabetes.",
  },
  {
    q: "Does fatty liver mean I will develop diabetes later?",
    a: "No. Fatty liver is associated with a higher risk of insulin resistance, prediabetes and type 2 diabetes, but progression is not inevitable. Your overall risk depends on multiple factors and may change with appropriate care.",
  },
  {
    q: "Can fatty liver be reversed?",
    a: "Liver fat can improve in many people when its metabolic drivers are addressed. The likelihood and extent of improvement depend on the stage and cause of liver disease. Scarring or advanced disease requires specialist assessment.",
  },
  {
    q: "What is the best fatty liver diet for diabetics?",
    a: "There is no single diet that suits everyone. A balanced eating pattern built around minimally processed foods, appropriate portions, fibre and suitable protein can help, but the plan should account for glucose-lowering medicines, kidney health and individual nutritional needs.",
  },
  {
    q: "Is elevated ALT or SGPT related to insulin resistance?",
    a: "It can be, but these enzymes can rise for many reasons. A clinician should interpret them alongside your history, medicines, alcohol intake, other blood tests and imaging where needed.",
  },
  {
    q: "Can a liver supplement lower blood sugar?",
    a: "A supplement should not be assumed to lower blood sugar or treat insulin resistance unless that specific effect is supported and the product is appropriately authorised for it. Monitor blood sugar and use prescribed treatment as directed by your clinician.",
  },
];

export const Route = createFileRoute("/blog/fatty-liver-insulin-resistance-diabetes")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: HEADLINE,
      ogDescription:
        "Fatty liver and insulin resistance can feed each other. What that means for prediabetes, type 2 diabetes, testing and everyday habits.",
      headline: HEADLINE,
      breadcrumbName: "Fatty Liver and Diabetes: The Insulin Resistance Connection",
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      faqs: FAQS,
    }),
});

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {children}
    </p>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
      {children}
    </h2>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-brand-deep md:text-2xl">
      {children}
    </h3>
  );
}

function Direct({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 rounded-2xl border border-border bg-card p-4 text-base leading-relaxed text-brand-deep md:text-[17px]">
      <span className="font-semibold">Quick answer: </span>
      {children}
    </p>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-4 space-y-2">
      {items.map((s) => (
        <li
          key={s}
          className="flex gap-3 text-base leading-relaxed text-muted-foreground"
        >
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          <span className="min-w-0 break-words">{s}</span>
        </li>
      ))}
    </ul>
  );
}

function Numbers({ items }: { items: string[] }) {
  return (
    <ol className="mt-4 space-y-2">
      {items.map((s, i) => (
        <li
          key={s}
          className="flex gap-3 text-base leading-relaxed text-muted-foreground"
        >
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[11px] font-bold text-brand">
            {i + 1}
          </span>
          <span className="min-w-0 break-words">{s}</span>
        </li>
      ))}
    </ol>
  );
}

const MASLD_COMPANIONS = [
  "insulin resistance",
  "prediabetes or type 2 diabetes",
  "high triglycerides or abnormal cholesterol",
  "high blood pressure",
  "excess abdominal or visceral fat",
];

const LOOP_STEPS = [
  "Insulin resistance can promote liver-fat accumulation.",
  "Excess liver fat may worsen the liver's response to insulin.",
  "The liver may release more glucose into the bloodstream.",
  "The pancreas may need to produce more insulin to compensate.",
  "In susceptible people, blood sugar can gradually rise toward prediabetes or type 2 diabetes.",
];

const DISCUSS_WITH_DOCTOR = [
  "prediabetes or type 2 diabetes",
  "high triglycerides, low HDL cholesterol or other lipid abnormalities",
  "high blood pressure",
  "increased waist circumference or visceral fat",
  "polycystic ovary syndrome (PCOS)",
  "obstructive sleep apnoea",
  "persistently abnormal liver enzymes",
  "a family history of type 2 diabetes or metabolic liver disease",
];

const TESTS = [
  "fasting blood glucose",
  "HbA1c, which reflects average blood glucose over roughly three months",
  "an oral glucose tolerance test in selected cases",
  "a lipid profile, including triglycerides and HDL cholesterol",
  "liver blood tests such as ALT and AST",
  "abdominal ultrasound or another liver-imaging test",
  "non-invasive fibrosis-risk assessment when clinically appropriate",
];

function Article() {
  return (
    <article className="mx-auto max-w-3xl px-4 pb-24 pt-8 md:pt-14">
      <ReadWithLlm title={HEADLINE} url={URL} kind="article" />

      <Reveal>
        <div className="text-center">
          <span className="inline-flex items-center rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
            Liver &amp; Gut
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-brand-deep md:text-5xl">
            {HEADLINE}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {DESCRIPTION}
          </p>
          <BlogByline
            author="TruWe Clinical Library"
            date={PUBLISHED}
            readingTime="12 min read"
          />
        </div>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
          <img
            src={heroImg}
            alt="Illustration of a liver alongside glucose molecules and a blood-sugar curve"
            width={1408}
            height={784}
            loading="eager"
            className="h-auto w-full"
          />
        </figure>
      </Reveal>


      <Reveal delay={0.05}>
        <section className="mt-10">
          <P>
            Maybe your ultrasound report says “fatty liver.” Maybe your HbA1c has
            quietly moved into the prediabetes range. Or perhaps both appeared in
            the same health check, leaving you with one obvious question: are
            fatty liver and diabetes connected?
          </P>
          <P>
            Yes — but not in the simple, one-causes-the-other way that social
            media often suggests.
          </P>
          <P>
            Your liver helps manage both fat and blood sugar. When excess fat
            builds up inside it, the liver may stop responding to insulin as well
            as it should. At the same time, insulin resistance can make it easier
            for more fat to collect in the liver. Each problem can feed the other.
          </P>
          <P>
            That sounds worrying, but it is not a prediction. Fatty liver does
            not mean diabetes is inevitable. Think of it as a metabolic warning
            light: a reason to look more closely at blood sugar, cholesterol,
            blood pressure, sleep and everyday habits with a qualified clinician.
          </P>
          <Direct>
            Fatty liver is associated with insulin resistance and a higher risk of
            developing prediabetes and type 2 diabetes. The relationship also
            works in reverse: insulin resistance and diabetes can increase the
            risk of liver-fat buildup.
          </Direct>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>First, what does “fatty liver” actually mean?</H2>
          <P>
            The name is quite literal: fatty liver means excess fat has collected
            inside liver cells. The form linked with metabolic health is now
            commonly called metabolic dysfunction-associated steatotic liver
            disease, or MASLD. You may know it by its older name, non-alcoholic
            fatty liver disease or NAFLD.
          </P>
          <P>MASLD is commonly found alongside:</P>
          <Bullets items={MASLD_COMPANIONS} />
          <P>
            Here is what catches many people off guard: early fatty liver often
            causes no obvious symptoms. You may feel completely normal and
            discover it only through a blood test, ultrasound or routine health
            check.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>And what is insulin resistance?</H2>
          <P>
            Think of insulin as a signal. It tells your cells to take glucose out
            of the bloodstream and use or store it. It also tells the liver when
            to store glucose and when to release it.
          </P>
          <P>
            With insulin resistance, that signal becomes easier for the body to
            ignore. Cells in the muscles, fat tissue and liver do not respond as
            effectively as they should, so the pancreas may compensate by making
            more insulin. Over time, blood sugar may begin to rise — sometimes
            moving from the normal range to prediabetes and, in some people, type
            2 diabetes.
          </P>
          <P>
            Insulin resistance is not diagnosed from symptoms alone. A clinician
            may consider your medical history, waist measurement, blood pressure,
            lipid profile, fasting glucose, HbA1c and other appropriate tests.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>The quiet two-way loop between fatty liver and insulin resistance</H2>
          <P>
            The liver works like a metabolic control room. It stores glucose after
            meals and releases it between meals. When excess fat accumulates
            inside the liver, that control can become less precise. The liver may
            continue releasing glucose even when the body already has enough.
          </P>
          <P>
            At the same time, insulin resistance elsewhere in the body can
            increase the flow of fatty acids to the liver and encourage the liver
            to make and store more fat.
          </P>
          <P>Put simply, the cycle can look like this:</P>
          <figure className="mt-6 overflow-hidden rounded-3xl border border-border">
            <img
              src={loopImg}
              alt="Diagram showing the two-way loop between liver fat and insulin resistance"
              width={1408}
              height={784}
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="px-4 py-3 text-xs text-muted-foreground">
              Liver fat and insulin resistance can influence each other in both
              directions.
            </figcaption>
          </figure>
          <Numbers items={LOOP_STEPS} />

          <P>
            This cycle helps explain the connection, but it does not tell us what
            will happen to any one person. Genes, sleep, medicines, activity,
            diet, body-fat distribution and other health conditions all matter
            too.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>Does fatty liver mean diabetes is next?</H2>
          <P>
            “Can fatty liver cause diabetes?” is exactly the kind of question
            people type into Google after reading an ultrasound report. The honest
            answer is: fatty liver may raise risk, but it is not destiny.
          </P>
          <P>
            Fatty liver does not act as a single, direct cause of type 2 diabetes.
            Both conditions share several drivers, including insulin resistance,
            genetics, abdominal fat, diet, physical inactivity and sleep-related
            metabolic problems. However, research and clinical guidance recognise
            MASLD as being associated with a higher risk of prediabetes and type 2
            diabetes. In people who already have prediabetes, fatty liver may be a
            sign of greater metabolic risk.
          </P>
          <P>
            The relationship also goes the other way. People with type 2 diabetes
            have a higher likelihood of MASLD, and diabetes can increase the risk
            that liver disease becomes more serious.
          </P>
          <P>
            So, is fatty liver a sign of prediabetes? Not necessarily. You can
            have fatty liver without prediabetes, and prediabetes without fatty
            liver. But finding either one is a sensible reason to check the bigger
            metabolic picture with a clinician.
          </P>

          <H3>Can fatty liver increase blood sugar?</H3>
          <P>
            It may contribute. When the liver becomes less responsive to insulin,
            it may not turn down glucose production as effectively after a meal or
            overnight. That can make blood-sugar control harder.
          </P>
          <P>
            But one high reading cannot tell you that fatty liver is responsible.
            A poor night’s sleep, illness, stress, medicines and meal timing can
            all affect blood sugar. Look for patterns with appropriate testing
            rather than trying to diagnose yourself from one report.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>When is the connection worth discussing with a doctor?</H2>
          <P>
            You do not need to wait for symptoms. It may be useful to discuss
            fatty liver and insulin resistance with a clinician if you have one or
            more of the following:
          </P>
          <Bullets items={DISCUSS_WITH_DOCTOR} />
          <P>
            And yes, people in smaller bodies can also develop fatty liver and
            insulin resistance. A person’s appearance is not a blood test or a
            liver scan.
          </P>

          <H3>What about PCOS, fatty liver and diabetes?</H3>
          <P>
            PCOS is often accompanied by insulin resistance and an increased risk
            of impaired glucose tolerance and type 2 diabetes. Because insulin
            resistance can also promote liver-fat buildup, some people with PCOS
            may have overlapping metabolic risk.
          </P>
          <P>
            PCOS does not automatically mean that someone has fatty liver, and
            fatty liver does not diagnose PCOS. Each condition needs its own
            appropriate clinical assessment.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>What tests might your doctor discuss?</H2>
          <P>
            There is no single “liver and insulin resistance test” that answers
            everything. Depending on your history and risk profile, a clinician
            may consider:
          </P>
          <Bullets items={TESTS} />
          <P>
            One important detail: normal ALT or AST results do not always exclude
            fatty liver. On the other hand, an elevated ALT or SGPT result does
            not prove that insulin resistance is the cause. A report needs
            context.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>What can you do for both liver health and blood sugar?</H2>
          <P>
            The encouraging part is that liver health and blood-sugar health do
            not require two completely separate lives. The same foundations often
            support both. Depending on your needs, a clinician or registered
            dietitian may recommend:
          </P>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={habitsImg}
              alt="Balanced Indian meal of dal, vegetables and whole grains beside walking shoes and a glass of water"
              width={1408}
              height={784}
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
            <figcaption className="px-4 py-3 text-xs text-muted-foreground">
              Everyday foundations — balanced plates, movement, hydration and sleep —
              support liver health and blood sugar together.
            </figcaption>
          </figure>



          <H3>1. Make everyday meals do more of the work</H3>
          <P>
            Vegetables, dal and other pulses, whole grains, nuts, seeds and
            suitable protein sources can form a balanced base. You do not need a
            mysterious “liver detox diet.” You need an eating pattern you can
            repeat — and one that fits your medicines, glucose response, activity
            and health goals.
          </P>

          <H3>2. Reduce frequent sources of added sugar</H3>
          <P>
            Sugary drinks, sweetened juices and heavily refined snacks can add
            substantial sugar without much fibre or fullness. Reducing them may
            support both blood-sugar and triglyceride management.
          </P>

          <H3>3. Choose movement you can keep doing</H3>
          <P>
            Walking, cycling, swimming and resistance training can support insulin
            sensitivity. The best routine is rarely the most punishing one. It is
            the one that still exists a month from now.
          </P>

          <H3>4. Skip the crash-diet trap</H3>
          <P>
            For people with higher weight, clinically appropriate and sustainable
            weight loss may reduce liver fat and improve metabolic markers. That
            does not make extreme diets a shortcut. Prolonged fasting or rapid
            weight-loss plans may be unsafe, particularly if you take diabetes
            medicines.
          </P>

          <H3>5. Protect sleep and review alcohol intake</H3>
          <P>
            Poor sleep and sleep apnoea can affect metabolic health. Alcohol can
            also contribute to liver injury, so discuss a suitable limit — or
            whether to avoid it — with your clinician.
          </P>

          <H3>6. Take prescribed treatment as directed</H3>
          <P>
            Do not stop or replace diabetes, cholesterol, blood-pressure or other
            prescribed medicines with a supplement. Treatment should be
            individualised by the clinician managing your health.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>Where can TruWe Liver Biome Plus fit?</H2>
          <P>
            Let us be clear about the order of importance. Food patterns, physical
            activity, sleep, clinical monitoring and prescribed care remain the
            foundations of managing fatty liver, insulin resistance, prediabetes
            and type 2 diabetes.
          </P>
          <P>
            TruWe Liver Biome Plus can be considered by adults looking for general
            nutritional support within a broader liver-wellness routine. Think of
            it as an optional layer — not the foundation. It is not a treatment
            for fatty liver, insulin resistance, prediabetes, PCOS or diabetes,
            and it should never replace medical care or prescribed medicine.
          </P>
          <P>
            Before using any supplement, check the current label and discuss it
            with a qualified clinician — particularly if you have diabetes, liver
            or gallbladder disease; are pregnant or breastfeeding; take blood
            thinners; or use glucose-lowering medicines.
          </P>
          <Link
            to="/products/$handle"
            params={{ handle: "liver-biome-plus" }}
            className="group mt-8 flex flex-col gap-2 rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-luxury"
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              From the TruWe range
            </p>
            <p className="font-display text-xl font-bold text-brand-deep group-hover:text-brand">
              Liver Biome Plus
            </p>
            <p className="text-sm text-muted-foreground">
              Liposomal liver actives with a targeted gut biome stack. Review the
              current ingredients, directions and safety information before use.
            </p>
            <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-brand">
              Explore TruWe Liver Biome Plus <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </Link>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>Frequently asked questions</H2>
          <div className="mt-6 space-y-6">
            {FAQS.map((f, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-lg font-bold text-brand-deep">
                  {f.q}
                </h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>The takeaway: treat fatty liver as information, not a verdict</H2>
          <P>
            Your liver is more than a “detox” organ. It is one of the body’s main
            managers of glucose and fat. Fatty liver and insulin resistance can
            reinforce each other, which helps explain the close relationship
            between prediabetes and fatty liver, as well as the high frequency of
            fatty liver in diabetic patients.
          </P>
          <P>
            The useful message is not “diabetes is coming.” It is “this is worth
            paying attention to.” A fatty-liver finding can be an early
            opportunity to look more closely at blood sugar, cholesterol, blood
            pressure, sleep and other metabolic risks — and to act with qualified
            medical guidance.
          </P>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12">
          <H2>References</H2>
          <ol className="mt-4 list-decimal space-y-3 pl-5 text-base leading-relaxed text-muted-foreground">
            <li>
              Centers for Disease Control and Prevention. Type 2 Diabetes and Your
              Liver.{" "}
              <a
                className="text-brand hover:underline"
                href="https://www.cdc.gov/diabetes/diabetes-complications/type-2-diabetes-liver-disease.html"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                cdc.gov
              </a>
            </li>
            <li>
              National Institute of Diabetes and Digestive and Kidney Diseases.
              Fatty Liver Disease: An Underdiagnosed Complication of Type 2
              Diabetes.{" "}
              <a
                className="text-brand hover:underline"
                href="https://www.niddk.nih.gov/health-information/professionals/diabetes-discoveries-practice/fatty-liver-disease-an-underdiagnosed-complication-of-type-2-diabetes"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                niddk.nih.gov
              </a>
            </li>
            <li>
              EASL–EASD–EASO. Clinical Practice Guidelines on the Management of
              Metabolic Dysfunction-Associated Steatotic Liver Disease.{" "}
              <a
                className="text-brand hover:underline"
                href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11299976/"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                PMC11299976
              </a>
            </li>
            <li>
              International Evidence-based Guideline for the Assessment and
              Management of Polycystic Ovary Syndrome 2023.{" "}
              <a
                className="text-brand hover:underline"
                href="https://academic.oup.com/jcem/article/108/10/2447/7242360"
                target="_blank"
                rel="noopener noreferrer nofollow"
              >
                J Clin Endocrinol Metab
              </a>
            </li>
          </ol>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <section className="mt-12 rounded-2xl border border-border bg-muted/50 p-5 text-sm leading-relaxed text-muted-foreground">
          <p>
            This article is for general education only and is not a substitute for
            diagnosis or medical advice. Consult a qualified healthcare
            professional about symptoms, test results, medicines and supplements.
          </p>
        </section>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-12">
          <ClinicalProductPitch cluster="liver-gut" />
          <ClusterLinks cluster="liver-gut" />
          <RelatedReading articles={relatedArticles(SLUG, 3)} />
          <AuthorityFlow cluster="liver-gut" />
        </div>
      </Reveal>
    </article>
  );
}
